import { Box, Button, Popover, Typography } from '@mui/material';
import { PencilSimpleLine, Trash } from '@phosphor-icons/react';
import React from 'react';
import { useDataSidebarContext } from './context';

interface ActionMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onRename?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  showEdit?: boolean;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl,
  onClose,
  onRename,
  onEdit,
  onDelete,
  showEdit = false,
}) => {
  const { isDarkMode, palette } = useDataSidebarContext();

  const buttonSx = {
    width: '100%',
    background: 'unset',
    justifyContent: 'flex-start',
    textTransform: 'unset' as const,
    fontSize: '16px',
    boxShadow: 'unset',
    px: '8px',
    gap: '8px',
    color: isDarkMode ? palette.gray[300] : palette.gray[600],
    '&:hover': {
      background: isDarkMode ? palette.gray[800] : palette.gray[200],
      boxShadow: 'unset',
    },
  };

  const iconColor = isDarkMode ? palette.gray[400] : palette.gray[500];
  const textColor = isDarkMode ? palette.gray[300] : palette.gray[600];

  const renderAction = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    customSx?: Record<string, unknown>,
  ) => (
    <Button
      variant='contained'
      onClick={() => {
        onClose();
        onClick();
      }}
      sx={{ ...buttonSx, ...customSx }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        {icon}
        <Typography sx={{ color: customSx?.color || textColor }}>
          {label}
        </Typography>
      </Box>
    </Button>
  );

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: { marginLeft: '8px' },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: '8px',
          background: isDarkMode ? palette.gray[900] : '#FFFFFF',
          minWidth: '180px',
        }}
      >
        {onRename &&
          renderAction(
            <PencilSimpleLine size={20} color={iconColor} />,
            'Rename',
            onRename,
          )}
        {showEdit &&
          onEdit &&
          renderAction(
            <PencilSimpleLine size={20} color={iconColor} />,
            'Edit',
            onEdit,
          )}
        {onDelete &&
          renderAction(
            <Trash size={20} color='#b91c1c' />,
            'Delete',
            onDelete,
            {
              color: '#b91c1c',
              '&:hover': {
                background: isDarkMode ? '#FFADB7' : '#fecdd3',
                boxShadow: 'unset',
              },
            },
          )}
      </Box>
    </Popover>
  );
};
